import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { requestAPI } from '../../data/api'
import './ContactActions.css'

class ContactActions extends Component {
	constructor(props) {
		super(props)
		this.state = {
			actions: [],
			loading: false,
			selectedClientId: '',
			selectedUserId: '',
			selectedCountry: '',
			availableClients: [],
			availableUsers: [],
			userFilterText: '',
			userDropdownOpen: false,
			undoing: {}, // Track undo state per action id
		}
		this.userFilterRef = React.createRef()
	}

	componentWillUnmount() {
		document.removeEventListener('mousedown', this.handleDocumentClick)
	}

	handleDocumentClick = (e) => {
		if (
			this.userFilterRef.current &&
			!this.userFilterRef.current.contains(e.target)
		) {
			this.setState({ userDropdownOpen: false })
		}
	}

	componentDidMount() {
		this.loadFilters()
		this.fetchActions()
		document.addEventListener('mousedown', this.handleDocumentClick)
	}

	loadFilters = async () => {
		try {
			// Get all clients from /hubs/main endpoint
			const clients = await requestAPI('/hubs/main', 'GET')
			const availableClients = Array.isArray(clients)
				? clients
						.map((client) => ({
							client_id: client.client_id,
							client_name: client.name || `Client ${client.client_id}`,
							client_short_name: client.short_name || '',
						}))
						.sort((a, b) =>
							a.client_name
								.toLowerCase()
								.localeCompare(b.client_name.toLowerCase()),
						)
				: []

			// Get the users that can perform contact actions from the API
			const users = await requestAPI(
				'/users?permission_levels=admin,agent',
				'GET',
			)
			const availableUsers = (Array.isArray(users) ? users : []).map(
				(user) => ({
					...user,
					name: [user.firstname, user.lastname].filter(Boolean).join(' '),
				}),
			)

			this.setState({ availableClients, availableUsers })
		} catch (error) {
			console.error('Failed to load filters:', error)
		}
	}

	fetchActions = async () => {
		this.setState({ loading: true })
		try {
			const { selectedClientId, selectedUserId } = this.state
			const params = new URLSearchParams()

			if (selectedClientId) {
				params.append('clientId', selectedClientId)
			}
			if (selectedUserId) {
				params.append('userId', selectedUserId)
			}

			const queryString = params.toString()
			const url = `/violations/contact/actions${
				queryString ? `?${queryString}` : ''
			}`

			const actions = await requestAPI(url, 'GET')
			this.setState({
				actions: Array.isArray(actions) ? actions : [],
				loading: false,
			})
		} catch (error) {
			console.error('Failed to fetch contact actions:', error)
			this.setState({ loading: false, actions: [] })
		}
	}

	handleClientChange = (e) => {
		this.setState({ selectedClientId: e.target.value }, () => {
			this.fetchActions()
		})
	}

	handleUserChange = (e) => {
		this.setState({ selectedUserId: e.target.value }, () => {
			this.fetchActions()
		})
	}

	handleUserFilterTextChange = (e) => {
		this.setState({ userFilterText: e.target.value, userDropdownOpen: true })
	}

	handleSelectUser = (user) => {
		this.setState(
			{
				selectedUserId: user.user_id,
				userFilterText: user.name || user.email || '',
				userDropdownOpen: false,
			},
			() => this.fetchActions(),
		)
	}

	handleClearUser = () => {
		this.setState(
			{ selectedUserId: '', userFilterText: '', userDropdownOpen: false },
			() => this.fetchActions(),
		)
	}

	handleCountryChange = (e) => {
		this.setState({ selectedCountry: e.target.value })
	}

	getClientName = (clientId) => {
		const client = this.state.availableClients.find(
			(c) => c.client_id === clientId,
		)
		return client ? client.client_name : `Client ${clientId}`
	}

	getUserName = (userId) => {
		const user = this.state.availableUsers.find((u) => u.user_id === userId)
		return user ? user.name || user.email : `User ${userId}`
	}

	formatDate = (timestamp) => {
		if (!timestamp) return '—'
		const date = new Date(timestamp)
		return date.toLocaleString('en-GB', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		})
	}

	getActionBackgroundColor = (action) => {
		// Only show color backgrounds if state is not contact_enrichment
		if (action.state === 'contact_enrichment') {
			return 'transparent'
		}

		const hasCompanyNumber = !!action.company_number
		const hasEmail =
			Array.isArray(action.email_addresses) && action.email_addresses.length > 0
		const hasPhone =
			Array.isArray(action.phone_numbers) && action.phone_numbers.length > 0
		const hasAddress = !!(action.address && action.zip && action.city)

		// If none of the required fields are provided, mark with red
		if (!hasCompanyNumber && !hasEmail && !hasPhone && !hasAddress) {
			return '#fcb4b4ff' // red background
		}

		// If company_number, email, and phone are missing, but address is provided, mark with yellow
		if (!hasCompanyNumber && !hasEmail && !hasPhone && hasAddress) {
			return '#faeaabff' // yellow background
		}

		// Otherwise, no special background (white/default)
		return 'transparent'
	}

	handleUndo = async (action) => {
		const violationId = action.violation_id
		if (!violationId) {
			alert('Cannot undo: violation ID not found')
			return
		}

		if (
			!window.confirm(
				`Are you sure you want to undo this action and set the violation back to contact enrichment state?`,
			)
		) {
			return
		}

		try {
			// Set undoing state
			this.setState((state) => ({
				undoing: { ...state.undoing, [action.id]: true },
			}))

			// Update violation state back to contact_enrichment
			await requestAPI(`/violations/${violationId}/state`, 'PATCH', {
				state: 'contact_enrichment',
			})

			// Refresh the actions list
			await this.fetchActions()

			// Clear undoing state
			this.setState((state) => ({
				undoing: { ...state.undoing, [action.id]: false },
			}))
		} catch (error) {
			console.error('Failed to undo action:', error)
			this.setState((state) => ({
				undoing: { ...state.undoing, [action.id]: false },
			}))
			alert('Failed to undo action. Please try again.')
		}
	}

	renderFieldValues = (action) => {
		const fields = [
			{ key: 'company_name', label: 'Company Name' },
			{ key: 'company_number', label: 'Company Number' },
			{ key: 'address', label: 'Address' },
			{ key: 'zip', label: 'ZIP' },
			{ key: 'city', label: 'City' },
			{ key: 'country', label: 'Country' },
			{ key: 'email_addresses', label: 'Email' },
			{ key: 'phone_numbers', label: 'Phone' },
			{ key: 'counterparty_type', label: 'Type' },
		]

		const values = []
		fields.forEach((field) => {
			const value = action[field.key]
			const display = Array.isArray(value) ? value.join(', ') : value

			if (display) {
				values.push(
					<span key={field.key} className='field-value'>
						<strong>{field.label}:</strong> {display}
					</span>,
				)
			}
		})

		return values.length > 0 ? (
			values
		) : (
			<span className='no-changes'>No information provided</span>
		)
	}

	render() {
		const {
			loading,
			actions,
			selectedClientId,
			selectedUserId,
			selectedCountry,
			availableClients,
			availableUsers,
		} = this.state

		// Sort users alphabetically by name, falling back to email
		const filteredAndSortedUsers = [...availableUsers].sort((a, b) => {
			const labelA = (a.name || a.email || '').toLowerCase()
			const labelB = (b.name || b.email || '').toLowerCase()
			return labelA.localeCompare(labelB)
		})

		// Get unique countries from actions
		const availableCountries = [
			...new Set(
				actions
					.map((action) => action.country)
					.filter((country) => country && country.trim() !== ''),
			),
		].sort()

		// Filter actions by selected country (client-side)
		let filteredActions = selectedCountry
			? actions.filter((action) => action.country === selectedCountry)
			: actions

		// Get only the last action for each case (by case_id)
		// Group actions by case_id and keep only the most recent one
		const actionsByCase = {}
		filteredActions.forEach((action) => {
			if (action.case_id) {
				// If we haven't seen this case yet, or this action is newer, use it
				if (
					!actionsByCase[action.case_id] ||
					new Date(action.occured_at) >
						new Date(actionsByCase[action.case_id].occured_at)
				) {
					actionsByCase[action.case_id] = action
				}
			}
		})

		// Convert back to array and sort by timestamp (newest first)
		filteredActions = Object.values(actionsByCase).sort(
			(a, b) => new Date(b.occured_at) - new Date(a.occured_at),
		)

		return (
			<div className='contact-actions-container'>
				<div className='contact-actions-header'>
					<Link to='/contact-enrichment' className='contact-actions-back'>
						&larr; Back to Contact Enrichment
					</Link>
					<h1 className='contact-actions-title'>Contact Actions History</h1>
					<p className='contact-actions-subtitle'>
						View recent contact information changes
					</p>
				</div>

				<div className='contact-actions-filters'>
					<div className='filter-group'>
						<label className='filter-label'>Filter by Client</label>
						<select
							className='filter-select'
							value={selectedClientId}
							onChange={this.handleClientChange}
						>
							<option value=''>All Clients</option>
							{availableClients.map((client) => (
								<option key={client.client_id} value={client.client_id}>
									{client.client_name}
									{client.client_short_name
										? ` (${client.client_short_name})`
										: ''}
								</option>
							))}
						</select>
					</div>

					<div className='filter-group' ref={this.userFilterRef}>
						<label className='filter-label'>Filter by User</label>
						<div className='user-filter-wrap'>
							<input
								type='text'
								className='filter-select'
								placeholder='All Users — type to filter'
								value={this.state.userFilterText}
								onChange={this.handleUserFilterTextChange}
								onFocus={() => this.setState({ userDropdownOpen: true })}
							/>
							{this.state.selectedUserId && (
								<button
									type='button'
									className='user-filter-clear'
									onClick={this.handleClearUser}
									aria-label='Clear user filter'
								>
									×
								</button>
							)}
							{this.state.userDropdownOpen &&
								(() => {
									const q = this.state.userFilterText.trim().toLowerCase()
									const matches = q
										? filteredAndSortedUsers.filter((user) => {
												const name = (user.name || '').toLowerCase()
												const email = (user.email || '').toLowerCase()
												return name.includes(q) || email.includes(q)
											})
										: filteredAndSortedUsers
									if (matches.length === 0) return null
									return (
										<div className='user-filter-dropdown'>
											{matches.map((user) => (
												<button
													key={user.user_id}
													type='button'
													className='user-filter-option'
													onClick={() => this.handleSelectUser(user)}
												>
													<div className='user-filter-name'>
														{user.name || user.email}
													</div>
													{user.name && user.email && (
														<div className='user-filter-email'>
															{user.email}
														</div>
													)}
												</button>
											))}
										</div>
									)
								})()}
						</div>
					</div>

					<div className='filter-group'>
						<label className='filter-label'>Filter by Country</label>
						<select
							className='filter-select'
							value={selectedCountry}
							onChange={this.handleCountryChange}
						>
							<option value=''>All Countries</option>
							{availableCountries.map((country) => (
								<option key={country} value={country}>
									{country}
								</option>
							))}
						</select>
					</div>
				</div>

				{loading && (
					<div className='contact-actions-loading'>
						<div className='loading-spinner'></div>
						<p>Loading actions...</p>
					</div>
				)}

				{!loading && (
					<div className='contact-actions-results'>
						{filteredActions.length > 0 ? (
							<div className='actions-list'>
								{filteredActions.map((action) => {
									const backgroundColor = this.getActionBackgroundColor(action)
									const isRejected = action.state === 'rejected'
									const canUndo =
										action.state === 'ready_for_export' || isRejected
									const isUndoing = this.state.undoing[action.id]
									return (
										<div
											key={action.id}
											className={`action-card${isRejected ? ' action-card--rejected' : ''}`}
											style={{ backgroundColor }}
										>
											<div className='action-header'>
												<span className='action-date'>
													{this.formatDate(action.occured_at)}
												</span>
												<span className='action-separator'>•</span>
												<span className='action-user'>
													{action.firstname && action.lastname
														? `${action.firstname} ${action.lastname}`
														: this.getUserName(action.user_id)}
												</span>
												{action.violation_id && (
													<>
														<span className='action-separator'>•</span>
														<Link
															to={`/violation/${action.violation_id}`}
															className='case-id-link'
														>
															{action.case_id ||
																action.violation_id.slice(0, 8)}
														</Link>
													</>
												)}
												{action.state && (
													<>
														<span className='action-separator'>•</span>
														<span
															className={`action-state${isRejected ? ' action-state--rejected' : ''}`}
														>
															{action.state}
														</span>
													</>
												)}
												{action.domain && (
													<>
														<span className='action-separator'>•</span>
														<a
															href={`https://${action.domain}`}
															target='_blank'
															rel='noopener noreferrer'
														>
															{action.domain}
														</a>
													</>
												)}
												{canUndo && (
													<>
														<span className='action-separator'>•</span>
														<button
															className='undo-button'
															onClick={() => this.handleUndo(action)}
															disabled={isUndoing}
														>
															{isUndoing ? 'Undoing...' : 'Undo'}
														</button>
													</>
												)}
											</div>
											<div className='action-details'>
												{this.renderFieldValues(action)}
											</div>
										</div>
									)
								})}
							</div>
						) : (
							<div className='no-actions'>
								<p>No contact actions found for the selected criteria.</p>
							</div>
						)}
					</div>
				)}
			</div>
		)
	}
}

ContactActions.contextTypes = {
	gettext: PropTypes.func.isRequired,
}

export default ContactActions
